/**
 * Come si chiamano in italiano i valori che viaggiano sul filo.
 *
 * Gli enum di `enums.ts` sono nomi tecnici — `UNSPECIFIED`, `DEPUTY` — e nessuno
 * di quei nomi va a schermo così com'è. Finora ogni schermata che ne mostrava uno
 * si scriveva la propria mappa: `RUOLO_NEL_REPARTO` nella scheda del dipendente,
 * un'altra uguale nell'elenco, una terza — inevitabilmente diversa di una parola —
 * nell'app. Tre copie di una traduzione sono tre occasioni di scrivere «vice» in un
 * posto e «vice responsabile» nell'altro, e nessun controllo se ne accorge.
 *
 * Qui stanno una volta sola, tipizzate su `Record<Enum, string>`: un membro nuovo
 * nell'enum non compila finché non gli si dà un nome italiano, che è esattamente il
 * momento in cui si sa come chiamarlo.
 *
 * **Solo le etichette che vanno a schermo**, e solo per gli enum di cui il nome
 * italiano è una scelta e non una traduzione ovvia. Gli stati che il pannello
 * accompagna già con una frase intera — approvazioni, note spese — restano dove
 * sono: una parola sola fuori contesto sarebbe più corta, non più chiara.
 */
import { DepartmentRole, DevicePlatform, Gender, TransferPriority } from './enums.js';
/**
 * Il genere, come si scrive in una scheda.
 *
 * `UNSPECIFIED` è «non indicato» e non «altro»: sono due cose diverse — la prima
 * dice che nessuno l'ha scritto, la seconda è una risposta che qualcuno ha dato —
 * e il vecchio sistema le confondeva, perché la sua colonna non aveva un valore
 * per la prima e usava il predefinito per entrambe.
 */
export const GENDER_LABELS = {
    [Gender.MALE]: 'Uomo',
    [Gender.FEMALE]: 'Donna',
    [Gender.OTHER]: 'Altro',
    [Gender.UNSPECIFIED]: 'Non indicato',
};
/** Che posto occupa una persona dentro il proprio reparto. */
export const DEPARTMENT_ROLE_LABELS = {
    [DepartmentRole.HEAD]: 'responsabile',
    [DepartmentRole.DEPUTY]: 'vice responsabile',
    [DepartmentRole.MEMBER]: 'membro',
};
/**
 * Quanto può aspettare uno spostamento.
 *
 * Le quattro parole sono quelle del vecchio pannello, riprese alla lettera perché
 * erano già giuste: ognuna dice un tempo — «entro sera», «entro 24 ore» — e non un
 * aggettivo. «Alta» da sola non si compila, «Alta (1-4 ore)» sì, perché chi apre il
 * ritiro sa se la merce gli serve prima di pranzo o dopodomani. Quello che cambia
 * rispetto a prima è dove stanno: nel bundle web nessuno fuori dal pannello poteva
 * leggerle, qui le legge anche il telefono.
 */
export const TRANSFER_PRIORITY_LABELS = {
    [TransferPriority.HIGH]: 'Alta (1-4 ore)',
    [TransferPriority.MODERATE]: 'Moderata (entro sera)',
    [TransferPriority.MEDIUM]: 'Media (entro 24 ore)',
    [TransferPriority.LOW]: 'Bassa (entro 3 giorni)',
};
/**
 * L'ordine della scala, dalla più urgente alla meno.
 *
 * Serve per ordinare una coda e per decidere quanto accendere una pastiglia. È un
 * numero e non l'ordine di dichiarazione dell'enum perché un enum di TypeScript non
 * promette un ordine a chi lo legge: `Object.values` lo dà, ma nessuno che scriva
 * `sort` dovrebbe fidarsi di quello. I numeri sono gli stessi 1..4 che stavano nella
 * colonna del vecchio sistema, così una riga importata resta confrontabile.
 */
export const TRANSFER_PRIORITY_RANK = {
    [TransferPriority.HIGH]: 1,
    [TransferPriority.MODERATE]: 2,
    [TransferPriority.MEDIUM]: 3,
    [TransferPriority.LOW]: 4,
};
/**
 * Che tipo di apparecchio è, per chi sta per prenderlo in mano o telefonargli.
 *
 * `IOS` non diventa «iPhone», e la rinuncia è voluta: la stessa parola arriva anche da
 * un iPad, e una schermata che scrive «iPhone» accanto a un tablet dice una cosa falsa
 * con l'aria di saperne. «Apple» è vero per tutti e due ed è come li chiama chi li ha
 * in tasca. Il modello esatto — quando il dispositivo lo dichiara — sta nella colonna
 * accanto, e lì è preciso perché lo scrive il telefono e non questa mappa.
 *
 * `WEB` è il pannello aperto in un browser: non è un telefono, e chiamarlo «Web»
 * lascerebbe il dubbio se sia un'app o una pagina.
 */
export const DEVICE_PLATFORM_LABELS = {
    [DevicePlatform.IOS]: 'Apple',
    [DevicePlatform.ANDROID]: 'Android',
    [DevicePlatform.WEB]: 'Browser',
};
//# sourceMappingURL=labels.js.map